
/*!
 * @pendo/components 3.25.3
 * (c) 2018-present
 */

import isNumber from 'lodash/isNumber';

const FOCUSABLE_ELEMENTS = ['a[href]', 'area[href]', 'input:not([disabled]):not([type="hidden"]):not([aria-hidden])', 'select:not([disabled]):not([aria-hidden])', 'textarea:not([disabled]):not([aria-hidden])', 'button:not([disabled]):not([aria-hidden])', 'iframe', 'object', 'embed', '[contenteditable]', '[tabindex]:not([tabindex^="-"])'];
function getStyle(el) {
  return window.getComputedStyle ? getComputedStyle(el, null) : el.currentStyle;
}
function getHeight(element) {
  const _getStyle2 = getStyle(element),
        height = _getStyle2.height;

  return parseFloat((height || '').replace('px', ''));
}
function setStyles(element, styles) {
  if (element) {
    Object.keys(styles).forEach(prop => {
      let unit = '';
      const numericUnits = ['minWidth', 'maxWidth', 'minHeight', 'maxHeight', 'width', 'height', 'top', 'right', 'bottom', 'left', 'marginLeft', 'marginRight', 'marginTop', 'marginBottom'];

      if (numericUnits.includes(prop) && isNumber(styles[prop])) {
        unit = 'px';
      }

      element.style[prop] = styles[prop] + unit;
    });
  }
}
function getMargin(el) {
  const style = window.getComputedStyle ? getComputedStyle(el, null) : el.currentStyle;
  return {
    marginLeft: parseInt(style.marginLeft) || 0,
    marginRight: parseInt(style.marginRight) || 0,
    marginTop: parseInt(style.marginTop) || 0,
    marginBottom: parseInt(style.marginBottom) || 0
  };
}
function setAttributes(element, attributes) {
  Object.keys(attributes).forEach(prop => {
    const value = attributes[prop];

    if (value !== false) {
      element.setAttribute(prop, attributes[prop]);
    } else {
      element.removeAttribute(prop);
    }
  });
}
function hasClass(element, cls) {
  return element && element.classList.contains(cls);
}
function removeClass(element, cls) {
  if (element && hasClass(element, cls)) {
    element.classList.remove(cls);
  }
}
function addClass(element, cls) {
  if (element && !hasClass(element, cls)) {
    element.classList.add(cls);
  }
}
function getTarget(node = document.body) {
  if (node === true) {
    return document.body;
  }

  return node instanceof window.Node ? node : document.querySelector(node);
}
function elementPositionInAncestor(el, ancestor) {
  let top = 0;
  let left = 0;

  do {
    top += el.offsetTop || 0;
    left += el.offsetLeft || 0;
    el = el.offsetParent;
  } while (el && el !== ancestor);

  return {
    top,
    left
  };
}
function getScrollParent(node) {
  const isElement = node instanceof HTMLElement;
  const overflowY = isElement && window.getComputedStyle(node).overflowY;
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';

  if (!node) {
    return null;
  }

  if (isScrollable && node.scrollHeight >= node.clientHeight) {
    return node;
  }

  return getScrollParent(node.parentNode) || document.body;
}
function raf(fn) {
  let lastArgs;
  let activeFrame = null;

  const wrapperFn = (...args) => {
    lastArgs = args;

    if (activeFrame) {
      return;
    }

    activeFrame = requestAnimationFrame(() => {
      activeFrame = null;
      fn(...lastArgs);
    });
  };

  wrapperFn.cancel = () => {
    if (!activeFrame) {
      return;
    }

    cancelAnimationFrame(activeFrame);
    activeFrame = null;
  };

  return wrapperFn;
}
function elementHasOverflowY({
  clientHeight,
  scrollHeight
}) {
  return scrollHeight > clientHeight;
}
function elementHasOverflowX({
  clientWidth,
  scrollWidth
}) {
  return scrollWidth > clientWidth;
}
function elementHasOverflow(element) {
  return elementHasOverflowY(element) || elementHasOverflowX(element);
}
function findNodeByContent(text, root = document.body) {
  const treeWalker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodeList = [];

  while (treeWalker.nextNode()) {
    const node = treeWalker.currentNode;

    if (node.nodeType === Node.TEXT_NODE && node.textContent.includes(text)) {
      nodeList.push(node.parentNode);
    }
  }

  return nodeList;
}
async function nextFrame() {
  return new Promise(resolve => {
    requestAnimationFrame(resolve);
  });
}
function getFocusableNodes(element = document) {
  const nodes = element.querySelectorAll(FOCUSABLE_ELEMENTS);
  return Array(...nodes);
}
function setFocusToFirstNode(element = document) {
  const focusableNodes = getFocusableNodes(element);

  if (!focusableNodes.length) {
    return;
  }

  focusableNodes[0].focus();
}
function trapFocus(event, focusTrapElement) {
  let focusableNodes = getFocusableNodes(focusTrapElement);

  if (!focusableNodes.length) {
    return;
  }

  focusableNodes = focusableNodes.filter(node => node.offsetParent !== null);
  const focusedItemIndex = focusableNodes.indexOf(document.activeElement);

  if (event.shiftKey && focusedItemIndex === 0) {
    focusableNodes[focusableNodes.length - 1].focus();
    event.preventDefault();
  }

  if (!event.shiftKey && focusableNodes.length > 0 && focusedItemIndex === focusableNodes.length - 1) {
    focusableNodes[0].focus();
    event.preventDefault();
  }
}
function isPercent(val) {
  return val && /^\d+%$/.test(val);
}

export { setAttributes as a, setFocusToFirstNode as b, getTarget as c, getMargin as d, elementHasOverflow as e, findNodeByContent as f, getScrollParent as g, elementPositionInAncestor as h, isPercent as i, removeClass as j, addClass as k, elementHasOverflowX as l, getHeight as m, nextFrame as n, raf as r, setStyles as s, trapFocus as t };
